<template>
  <div class="view quiz">

    <!-- Gender screen -->
    <transition name="slide" mode="out-in">
      <div v-if="!genderSelected" key="gender" class="quiz__screen">
        <p class="quiz__step orbitron">PASUL 2 / 3 — PROFILUL TĂU</p>
        <h2 class="quiz__question orbitron">Ești...?</h2>

        <div class="gender-cards">
          <button
            class="gender-card"
            :class="{ 'gender-card--active': pendingGender === 'male' }"
            @click="selectGender('male')"
          >
            <span class="gender-card__icon">♂</span>
            <span class="gender-card__label orbitron">BĂIAT</span>
          </button>
          <button
            class="gender-card"
            :class="{ 'gender-card--active': pendingGender === 'female' }"
            @click="selectGender('female')"
          >
            <span class="gender-card__icon">♀</span>
            <span class="gender-card__label orbitron">FATĂ</span>
          </button>
        </div>
      </div>

      <!-- Quiz questions -->
      <div v-else key="quiz" class="quiz__screen">
        <div class="quiz__progress-bar">
          <div class="quiz__progress-fill" :style="{ width: progressPct + '%' }"></div>
        </div>
        <p class="quiz__step orbitron">ÎNTREBAREA {{ currentIndex + 1 }} / {{ questions.length }}</p>

        <transition name="question-slide" mode="out-in">
          <div :key="currentIndex" class="quiz__q-block">
            <h2 class="quiz__question orbitron">{{ currentQuestion.question }}</h2>
            <p v-if="currentQuestion.subtitle" class="quiz__subtitle orbitron">{{ currentQuestion.subtitle }}</p>
            <div class="answer-grid">
              <button
                v-for="(ans, i) in currentQuestion.answers"
                :key="i"
                class="answer-card"
                :class="{ 'answer-card--selected': selectedAnswer === i }"
                @click="selectAnswer(i)"
              >
                <img v-if="ans.image" :src="ans.image" class="answer-card__img" :alt="ans.text" />
                <QuizIcon v-else :name="ans.icon" class="answer-card__icon" />
                <span class="answer-card__text orbitron">{{ ans.text }}</span>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/store/session'
import { QUIZ_QUESTIONS, calculatePersona } from '@/data/questions'
import QuizIcon from '@/components/QuizIcon.vue'

const router = useRouter()
const session = useSessionStore()

const questions = QUIZ_QUESTIONS
const genderSelected = ref(false)
const pendingGender = ref(null)
const currentIndex = ref(0)
const selectedAnswer = ref(null)
const answers = ref([])

const currentQuestion = computed(() => questions[currentIndex.value])
const isLast = computed(() => currentIndex.value === questions.length - 1)
const progressPct = computed(() => ((currentIndex.value) / questions.length) * 100)

// Fără buton CONTINUĂ — alegerea avansează direct, cu un mic delay ca selecția
// să fie vizibilă (feedback), și cu gardă anti-dublu-click în timpul tranziției.
const advancing = ref(false)

function selectGender(g) {
  if (advancing.value) return
  advancing.value = true
  pendingGender.value = g

  setTimeout(() => {
    session.gender = g
    genderSelected.value = true
    advancing.value = false
  }, 350)
}

function selectAnswer(i) {
  if (advancing.value) return
  advancing.value = true
  selectedAnswer.value = i

  setTimeout(() => {
    answers.value.push(currentQuestion.value.answers[i])

    if (isLast.value) {
      session.quizAnswers = answers.value
      session.personaId = calculatePersona(answers.value)
      router.push('/processing')
    } else {
      currentIndex.value++
      selectedAnswer.value = null
    }
    advancing.value = false
  }, 400)
}
</script>

<style scoped>
.quiz {
  padding: 2.5rem 4rem;
  gap: 1.5rem;
}

.quiz__screen {
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.quiz__progress-bar {
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}

.quiz__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #7c3aed, #60a5fa);
  transition: width 0.4s ease;
  box-shadow: 0 0 8px rgba(139, 92, 246, 0.7);
}

.quiz__step {
  font-size: 0.8rem;
  letter-spacing: 0.4em;
  color: rgba(139, 92, 246, 0.7);
}

.quiz__question {
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  color: #fff;
  text-shadow: 0 0 30px rgba(139, 92, 246, 0.5);
}

.quiz__subtitle {
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  margin-top: -0.6rem;
}

/* ─── Gender cards ─── */
.gender-cards {
  display: flex;
  gap: 3rem;
}

.gender-card {
  width: 220px;
  height: 220px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.04);
  border: 2px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.gender-card:active,
.gender-card--active {
  border-color: rgba(139, 92, 246, 0.8);
  background: rgba(139, 92, 246, 0.12);
  box-shadow: 0 0 40px rgba(139, 92, 246, 0.3);
}

.gender-card__icon {
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.7);
}

.gender-card--active .gender-card__icon { color: #fff; }

.gender-card__label {
  font-size: 1.2rem;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.5);
}

.gender-card--active .gender-card__label { color: #fff; }

/* ─── Answer grid ─── */
.quiz__q-block {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.answer-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  width: 100%;
}

.answer-card {
  padding: 2.2rem 0.8rem 1.6rem;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1.4rem;
  cursor: pointer;
  transition: all 0.25s ease;
  min-height: 220px;
  /* bordură în gradient albastru→violet, ca în referință */
  border: 1.5px solid transparent;
  background:
    linear-gradient(rgba(9, 9, 22, 0.88), rgba(9, 9, 22, 0.88)) padding-box,
    linear-gradient(165deg, rgba(96, 165, 250, 0.55), rgba(139, 92, 246, 0.4) 45%, rgba(168, 85, 247, 0.55)) border-box;
}

.answer-card:active,
.answer-card--selected {
  background:
    linear-gradient(rgba(28, 16, 54, 0.92), rgba(28, 16, 54, 0.92)) padding-box,
    linear-gradient(165deg, #60a5fa, #a855f7) border-box;
  box-shadow: 0 0 34px rgba(139, 92, 246, 0.45), inset 0 0 24px rgba(139, 92, 246, 0.12);
  transform: translateY(-6px);
}

.answer-card__icon {
  width: 72px;
  height: 72px;
  margin: auto 0;
  transition: filter 0.25s ease, transform 0.25s ease;
}

.answer-card--selected .answer-card__icon {
  filter: drop-shadow(0 0 12px rgba(168, 85, 247, 0.95));
  transform: scale(1.08);
}

/* PNG-urile neon au fundal negru plin — mix-blend-mode: screen face negrul
   invizibil pe cardul întunecat și păstrează doar glow-ul. */
.answer-card__img {
  width: 100%;
  max-width: 150px;
  height: 90px;
  object-fit: contain;
  margin: auto 0;
  mix-blend-mode: screen;
  transition: transform 0.25s ease, filter 0.25s ease;
}

.answer-card--selected .answer-card__img {
  transform: scale(1.1);
  filter: brightness(1.25);
}

.answer-card__text {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  text-align: center;
  letter-spacing: 0.14em;
  line-height: 1.5;
}

.answer-card--selected .answer-card__text { color: #fff; }

.slide-enter-active,
.slide-leave-active { transition: all 0.35s ease; }
.slide-enter-from { opacity: 0; transform: translateX(30px); }
.slide-leave-to { opacity: 0; transform: translateX(-30px); }

.question-slide-enter-active,
.question-slide-leave-active { transition: all 0.3s ease; }
.question-slide-enter-from { opacity: 0; transform: translateY(20px); }
.question-slide-leave-to { opacity: 0; transform: translateY(-20px); }
</style>
